import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {Box} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import React, {useCallback, useRef, useState, useMemo, useLayoutEffect} from 'react'

import {GANTT_CHART_ROW_HEIGHT} from '../constants'
import {useCurrentTime} from '../hooks/useCurrentTime'
import {useDataGridColors} from '../hooks/useDataGridColors'
import {isStartOfDay} from '../selectors/time'
import {AddGanttChartItemProps, GanttChartBaseRow, GanttChartColorConfig} from '../types/common'

import {Canvas2D} from './canvas/Canvas2D'
import {GanttChartAddItem} from './GanttChartAddItem'

interface GanttChartDataGridProps<T extends GanttChartBaseRow, M extends Moment | number> {
  xAxis: M[]
  canvasOffset?: number
  chartStartBoundary: M
  chartEndBoundary: M
  stepWidth: number
  isDisabled: boolean
  ganttChartRows: T[]
  onAddItem?: (props: AddGanttChartItemProps<M>) => void | Promise<void>
  displayCurrentTime?: {
    timezoneId?: string
    nowOffset: number
  }
  isAddItemEnabled: boolean
  rowHeight?: number
  canvasRef?: React.RefObject<HTMLCanvasElement>
  colorConfig?: GanttChartColorConfig[]
  onRenderCallback?: (ctx: CanvasRenderingContext2D) => void
}

type SelectedCellPosition = {x: number; y: number}

export function GanttChartDataGrid<T extends GanttChartBaseRow, M extends Moment | number>({
  xAxis,
  canvasOffset,
  chartStartBoundary,
  chartEndBoundary,
  stepWidth,
  isDisabled,
  ganttChartRows,
  displayCurrentTime,
  onAddItem,
  isAddItemEnabled,
  rowHeight,
  canvasRef: externalCanvasRef,
  colorConfig,
  onRenderCallback
}: GanttChartDataGridProps<T, M>) {
  const [selectedCellPosition, setSelectedCellPosition] = useState<
    SelectedCellPosition | undefined
  >(undefined)
  const {timezoneId, nowOffset} = displayCurrentTime ?? {}
  const now = useCurrentTime({timezoneId})
  const nowRounded = useMemo(() => (now ? roundTo15MinIntervalStart(now) : undefined), [now])

  const addGanttChartItemRef = useRef<HTMLDivElement>(null)

  const ganttChartRowHeight = rowHeight ?? GANTT_CHART_ROW_HEIGHT

  const canvasHeight = ganttChartRows.length * ganttChartRowHeight
  const canvasWidth = stepWidth * xAxis.length

  const colors = useDataGridColors()

  const xAxisLength = xAxis.length

  const internalCanvasRef = useRef<HTMLCanvasElement>(null)

  const canvasRef = externalCanvasRef ?? internalCanvasRef

  const onCanvasMouseMove: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const yPart = y / GANTT_CHART_ROW_HEIGHT
      const xPart = (x / canvasWidth) * xAxisLength

      const xItem = Math.floor(xPart)
      const yItem = Math.floor(yPart)

      const yPartInsideCell = yPart - yItem
      const xPartInsideCell = xPart - xItem

      const selectedElement: SelectedCellPosition | undefined =
        xPartInsideCell > 0.1 &&
        xPartInsideCell < 0.9 &&
        yPartInsideCell > 0.1 &&
        yPartInsideCell < 0.9
          ? {x: xItem, y: yItem}
          : undefined

      if (isAddItemEnabled) setSelectedCellPosition(selectedElement)
    },
    [canvasRef, canvasWidth, isAddItemEnabled, xAxisLength]
  )
  const onCanvasMouseLeave: React.MouseEventHandler<HTMLDivElement> = useCallback(() => {
    setSelectedCellPosition(undefined)
  }, [])

  const getIsCellDisabled = (value: M | undefined) => {
    if (isDisabled || value === null || value === undefined) {
      return true
    }

    if (moment.isMoment(value)) {
      return displayCurrentTime
        ? value.isBefore(nowRounded) || value.isSameOrAfter(chartEndBoundary)
        : value.isSameOrAfter(chartEndBoundary)
    } else if (typeof value === 'number' && typeof chartEndBoundary === 'number') {
      return value > chartEndBoundary
    }

    return false
  }

  const canClickGridItem =
    !isDisabled &&
    selectedCellPosition !== undefined &&
    !getIsCellDisabled(xAxis[selectedCellPosition.x])

  const selectedItemId =
    selectedCellPosition !== undefined ? ganttChartRows?.[selectedCellPosition.y].id : undefined

  const relativeCanvasOffset = (canvasOffset ?? 0) * stepWidth
  const relativeNowOffset = (nowOffset ?? 0) - relativeCanvasOffset

  const renderCanvas = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const xAxisLength = xAxis.length
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
      ctx.fillStyle = colors.gridBgFuture
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)
      if (relativeNowOffset > 0) {
        ctx.fillStyle = colors.gridBgPast
        ctx.fillRect(0, 0, relativeNowOffset, canvasHeight)
      }

      const strokeWidth = 1

      // Render custom color palette over gantt chart
      colorConfig?.forEach((colorConfigItem) => {
        ctx.fillStyle = colorConfigItem.color
        colorConfigItem.rowIds.forEach((rowId) => {
          const startIndex = ganttChartRows.findIndex((row) => row.id === rowId)
          const startHeight = startIndex * ganttChartRowHeight
          ctx.fillRect(
            colorConfigItem.from,
            startHeight,
            colorConfigItem.to,
            startHeight + ganttChartRowHeight - strokeWidth
          )
        })
      })

      ctx.strokeStyle = colors.gridLineX
      ctx.lineWidth = strokeWidth
      for (let i = 0; i < ganttChartRows.length; i++) {
        ctx.beginPath()
        ctx.moveTo(0, i * ganttChartRowHeight)
        ctx.lineTo(canvasWidth, i * ganttChartRowHeight)
        ctx.stroke()
      }
      for (let i = 0; i < xAxisLength; i++) {
        const hour = xAxis[i]
        // drawing start of day lines more visible
        ctx.strokeStyle =
          moment.isMoment(hour) && isStartOfDay(hour) ? colors.gridDayStart : colors.gridLineY

        ctx.beginPath()

        const xPos = ((canvasWidth - 2 * strokeWidth) * i) / xAxisLength - strokeWidth / 2
        ctx.moveTo(xPos, 0)
        ctx.lineTo(xPos, canvasHeight)
        ctx.stroke()
      }

      if (onRenderCallback) onRenderCallback(ctx)
    },
    [
      xAxis,
      canvasWidth,
      canvasHeight,
      colors,
      relativeNowOffset,
      colorConfig,
      ganttChartRows,
      ganttChartRowHeight,
      onRenderCallback
    ]
  )

  useLayoutEffect(() => {
    if (
      selectedCellPosition !== undefined &&
      addGanttChartItemRef.current &&
      selectedItemId !== undefined
    ) {
      addGanttChartItemRef.current.style.transform = `translate(${
        selectedCellPosition.x * stepWidth
      }px , ${selectedCellPosition.y * GANTT_CHART_ROW_HEIGHT}px)`
      addGanttChartItemRef.current['data-position'] = `${selectedItemId}_${selectedCellPosition.x}`
    }
  }, [selectedCellPosition, selectedItemId, stepWidth])

  const selectedItemXAxisValue =
    selectedCellPosition !== undefined ? xAxis[selectedCellPosition.x] : undefined
  return (
    <Box
      sx={{
        display: 'block',
        height: `${canvasHeight - 2}px`,
        width: `${canvasWidth - 2}px`
      }}
      onMouseMove={onCanvasMouseMove}
      onMouseLeave={onCanvasMouseLeave}
      {...dataTestId('gantt_chart_background_grid')}
    >
      <Canvas2D
        renderCanvas={renderCanvas}
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
      />
      {isAddItemEnabled &&
        canClickGridItem &&
        selectedItemId !== undefined &&
        selectedItemXAxisValue !== undefined && (
          <GanttChartAddItem<M>
            ref={addGanttChartItemRef}
            chartStartBoundary={chartStartBoundary}
            chartEndBoundary={chartEndBoundary}
            stepWidth={stepWidth}
            rowId={selectedItemId}
            selectedItemXAxisValue={selectedItemXAxisValue}
            onAddItem={onAddItem}
          />
        )}
    </Box>
  )
}

GanttChartDataGrid.displayName = 'GanttChartDataGrid'
